import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchVerification,
  requestMailBack,
  fetchMailBackStatus,
  recordEvent,
} from '../lib/api.js';
import { useConsumerSession } from '../hooks/useConsumerSession.js';
import { useConsumerAuth } from '../context/ConsumerAuthContext.jsx';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipcode: '',
  country: 'US',
};

function MailBack() {
  const { sessionId, clearSession } = useConsumerSession();
  const { user } = useConsumerAuth();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formValues, setFormValues] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [trackingInput, setTrackingInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  useEffect(() => {
    if (!sessionId) return;

    let active = true;
    async function loadVerification() {
      setLoading(true);
      try {
        const data = await fetchVerification(sessionId);
        if (!active) return;
        setVerification(data);
        setFormValues((prev) => ({
          ...prev,
          email: data.email || prev.email,
        }));
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadVerification();

    return () => {
      active = false;
    };
  }, [sessionId]);

  useEffect(() => {
    if (user) {
      setFormValues((prev) => ({
        ...prev,
        email: prev.email || user.email || '',
        firstName: prev.firstName || user.name || '',
        zip: prev.zip || user.zip || '',
      }));
    }
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!sessionId) {
      setError('We could not find an active verification session.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await requestMailBack({
        sessionId,
        ...formValues,
      });
      setRequestResult(result);
      await recordEvent({
        eventType: 'mailback_created',
        sessionId,
        payload: { trackingNumber: result.trackingNumber },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    setLookupError('');
    setLookupResult(null);
    if (!trackingInput.trim()) {
      setLookupError('Enter the tracking number from your mail-back email.');
      return;
    }
    setLookupLoading(true);
    try {
      const status = await fetchMailBackStatus(trackingInput.trim());
      setLookupResult(status);
      await recordEvent({
        eventType: 'mailback_status_lookup',
        sessionId: status.verificationId,
        payload: { trackingNumber: trackingInput.trim() },
      });
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLookupLoading(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="space-y-6">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body space-y-5">
            <h1 className="card-title text-2xl text-primary">Track an existing mail-back</h1>
            <form onSubmit={handleLookup} className="space-y-4">
              <div className="form-control">
                <label className="label" htmlFor="tracking">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">Tracking number</span>
                </label>
                <input
                  id="tracking"
                  className="input input-bordered rounded-xl"
                  value={trackingInput}
                  onChange={(event) => setTrackingInput(event.target.value)}
                  placeholder="e.g. 9400 1000 0000 0000 0000 00"
                />
              </div>
              {lookupError ? (
                <div className="alert alert-error text-sm">
                  <span>{lookupError}</span>
                </div>
              ) : null}
              <button type="submit" className="btn btn-primary w-full rounded-full sm:w-auto" disabled={lookupLoading}>
                {lookupLoading ? 'Checking…' : 'Check mail-back status'}
              </button>
            </form>
            {lookupResult ? (
              <div className="space-y-3 rounded-2xl border border-success/30 bg-success/10 p-4 text-sm text-neutral">
                <p className="font-semibold text-success">Status: {lookupResult.status}</p>
                <p>
                  {lookupResult.brandName} — {lookupResult.productName}
                </p>
                <ul className="space-y-2 text-xs">
                  {(lookupResult.statusEvents || []).map((eventItem, index) => (
                    <li key={index} className="rounded-xl bg-base-100/80 p-3">
                      <p className="font-medium text-neutral">{eventItem.status}</p>
                      {eventItem.at ? <p className="text-neutral/80">{new Date(eventItem.at).toLocaleString()}</p> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <Link to="/verify" className="btn btn-outline rounded-full">
                Start verification
              </Link>
              <Link to="/learn" className="btn btn-outline rounded-full">
                See guidelines
              </Link>
            </div>
          </div>
        </div>
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body space-y-4">
            <h2 className="card-title text-xl text-primary">Need a new label?</h2>
            <p className="text-sm text-neutral">
              Verify your packaging to connect it with a brand before requesting a mail-back label.
            </p>
            <Link to="/verify" className="btn btn-primary w-fit rounded-full">
              Go to verification
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body text-sm text-neutral">Loading your verification details…</div>
      </div>
    );
  }

  if (verification && !['PACT', 'PACT_AND_OTHER'].includes(verification.outcome)) {
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-4">
          <h1 className="card-title text-2xl text-primary">Mail-back not required</h1>
          <p className="text-sm text-neutral">
            Based on the verification outcome (<span className="font-semibold">{verification.outcome}</span>),
            this item is better suited for {verification.outcome === 'CURBSIDE' ? 'curbside recycling' : 'responsible disposal'}.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/verify" className="btn btn-outline rounded-full">
              Try another item
            </Link>
            <button type="button" className="btn btn-outline rounded-full" onClick={clearSession}>
              Reset session
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requestResult) {
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-5">
          <h1 className="card-title text-2xl text-primary">Label on its way!</h1>
          <p className="text-sm text-neutral">
            We emailed your label to <span className="font-medium text-neutral">{formValues.email}</span>.
            You can also download it directly below.
          </p>
          <div className="space-y-2 rounded-2xl border border-success/30 bg-success/10 p-4 text-sm text-neutral">
            <p className="font-semibold text-success">Tracking number: {requestResult.trackingNumber}</p>
            <a href={requestResult.labelUrl} className="link link-primary" target="_blank" rel="noopener noreferrer">
              Download shipping label
            </a>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/verify" className="btn btn-outline rounded-full">
              Verify another item
            </Link>
            <button type="button" className="btn btn-outline rounded-full" onClick={clearSession}>
              Clear session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-4">
          <h2 className="card-title text-xl text-primary">Track an existing mail-back</h2>
          <form onSubmit={handleLookup} className="space-y-4">
            <div className="form-control">
              <label className="label" htmlFor="tracking-active">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">Tracking number</span>
              </label>
              <input
                id="tracking-active"
                className="input input-bordered rounded-xl"
                value={trackingInput}
                onChange={(event) => setTrackingInput(event.target.value)}
                placeholder="From your confirmation email"
              />
            </div>
            {lookupError ? (
              <div className="alert alert-error text-sm">
                <span>{lookupError}</span>
              </div>
            ) : null}
            <button type="submit" className="btn btn-outline rounded-full" disabled={lookupLoading}>
              {lookupLoading ? 'Checking…' : 'Check status'}
            </button>
          </form>
          {lookupResult ? (
            <div className="space-y-3 rounded-2xl border border-success/30 bg-success/10 p-4 text-sm text-neutral">
              <p className="font-semibold text-success">Status: {lookupResult.status}</p>
              <p>
                {lookupResult.brandName} — {lookupResult.productName}
              </p>
              <ul className="space-y-2 text-xs">
                {(lookupResult.statusEvents || []).map((eventItem, index) => (
                  <li key={index} className="rounded-xl bg-base-100/80 p-3">
                    <p className="font-medium text-neutral">{eventItem.status}</p>
                    {eventItem.at ? <p className="text-neutral/80">{new Date(eventItem.at).toLocaleString()}</p> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      <div>
        <h1 className="text-3xl font-semibold text-primary">Request a mail-back label</h1>
        <p className="mt-2 max-w-2xl text-sm text-neutral">
          Share your shipping address so we can generate a prepaid USPS label. Please send your package within 14 days of printing the label.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-primary">Packaging summary</h2>
            <p className="mt-1 text-sm text-neutral">
              {verification?.brandName} — {verification?.productName}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="form-control">
              <label className="label" htmlFor="firstName">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">First name</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                className="input input-bordered rounded-xl"
                value={formValues.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="lastName">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">Last name</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                className="input input-bordered rounded-xl"
                value={formValues.lastName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="email">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">Email</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="input input-bordered rounded-xl"
                value={formValues.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="addressLine1">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">Address line 1</span>
              </label>
              <input
                id="addressLine1"
                name="addressLine1"
                className="input input-bordered rounded-xl"
                value={formValues.addressLine1}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="addressLine2">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">
                  Address line 2 (optional)
                </span>
              </label>
              <input
                id="addressLine2"
                name="addressLine2"
                className="input input-bordered rounded-xl"
                value={formValues.addressLine2}
                onChange={handleChange}
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="city">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">City</span>
              </label>
              <input
                id="city"
                name="city"
                className="input input-bordered rounded-xl"
                value={formValues.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="state">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">
                  State / Territory (2-letter)
                </span>
              </label>
              <input
                id="state"
                name="state"
                className="input input-bordered rounded-xl uppercase"
                value={formValues.state}
                onChange={handleChange}
                maxLength={2}
                required
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="zipcode">
                <span className="label-text uppercase tracking-[0.14em] text-neutral">ZIP code</span>
              </label>
              <input
                id="zipcode"
                name="zipcode"
                className="input input-bordered rounded-xl"
                value={formValues.zipcode}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {error ? (
            <div className="alert alert-error text-sm">
              <span>{error}</span>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" className="btn btn-primary rounded-full" disabled={submitting}>
              {submitting ? 'Generating label…' : 'Create mail-back label'}
            </button>
            <button type="button" className="btn btn-outline rounded-full" onClick={clearSession}>
              Reset session
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default MailBack;
